<template>
  <div id="leaflet-map"></div>
</template>

<script>
import genMap from '@/utils/gen-map'
import L from 'leaflet'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'
import Input from 'vuetify-admin/src/mixins/input'

export default {
  mixins: [Input],
  props: {
    multiple: {
      type: Boolean,
      default: false,
    },
    zoom: {
      type: Number,
      default: null,
    },
    required: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    polylineCoords() {
      if (!this.input) {
        return []
      }

      if (this.input.type.toUpperCase() === 'MULTILINESTRING') {
        return this.input.coordinates
      } else {
        return [this.input.coordinates]
      }
    },
    center() {
      return this.polylineCoords.length > 0
        ? this.polylineCoords[0][0]
        : this.$admin.options.map.coords
    },
  },
  watch: {
    polylineCoords() {
      this.reloadPolyline()
    },
  },
  data() {
    return {
      map: null,
      editableLayers: null,
    }
  },
  mounted() {
    this.initializeMap()
    this.reloadPolyline()
  },
  methods: {
    initializeMap() {
      this.map = L.map('leaflet-map').setView(
        this.center,
        this.zoom || this.$admin.options.map.zoom
      )

      genMap(this.map, L, this.$admin.options.map.maxZoom)

      this.editableLayers = new L.FeatureGroup()
      this.map.addLayer(this.editableLayers)

      const drawPluginOptions = {
        position: 'topright',
        draw: {
          polyline: true,
          polygon: false,
          rectangle: false,
          circle: false,
          marker: false,
          circlemarker: false,
        },
        edit: {
          featureGroup: this.editableLayers,
          remove: true,
        },
      }

      const drawControl = new L.Control.Draw(drawPluginOptions)
      this.map.addControl(drawControl)

      this.map
        .on('draw:drawstart', this.onDrawStart)
        .on('draw:created', this.onCreated)
        .on('draw:edited', this.onEdited)
        .on('draw:deleted', this.onDeleted)
    },
    reloadPolyline() {
      this.editableLayers.eachLayer((layer) => {
        this.editableLayers.removeLayer(layer)
      })

      if (this.polylineCoords.length > 0) {
        this.polylineCoords.forEach((coords) => {
          const polyline = L.polyline(coords)
          this.editableLayers.addLayer(polyline)
        })
      }
    },
    onDrawStart() {
      if (this.multiple) {
        return
      }

      /**
       * Remove all layers before drawing if only one Polyline is allowed
       */
      this.editableLayers.eachLayer((layer) => {
        this.editableLayers.removeLayer(layer)
      })
    },
    onCreated(e) {
      this.editableLayers.addLayer(e.layer)
      this.updatePolylineCoords()
    },
    onEdited() {
      this.updatePolylineCoords()
    },
    onDeleted() {
      this.updatePolylineCoords()
    },
    updatePolylineCoords() {
      const latLngGroup = []
      this.editableLayers.eachLayer((layer) => {
        latLngGroup.push(layer.getLatLngs())
      })

      if (latLngGroup.length === 0) {
        this.update(null)
        return
      }

      const coordinates = latLngGroup.map((group) => this.convertLatLngs(group))
      /**
       * Update the input value which will trigger reloading the line
       */
      const input = this.multiple
        ? { type: 'MultiLineString', coordinates }
        : { type: 'LineString', coordinates: coordinates[0] }
      this.update(input)
    },
    convertLatLngs(latLngGroup) {
      const coordinates = []
      latLngGroup.map((latLng) => {
        coordinates.push([latLng.lat, latLng.lng])
      })
      return coordinates
    },
  },
}
</script>
